import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Quote,
  AlertCircle,
  MapPin,
  ShieldCheck,
  FileCheck,
  Lock,
} from 'lucide-react';
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
  AnimatedBlobs,
  AnimatedCounter,
} from './AnimationComponents';
import { SchemaOrg, buildServiceSchema, buildFAQSchema, buildBreadcrumbSchema } from './SchemaOrg';

/**
 * Master Service Page Template
 * Renders the standard 9-section service page layout used across BRT.
 *
 * Sections (in order):
 * 1. Hero (headline + sub + 2 CTAs)
 * 2. Problem Bar (3 pain points)
 * 3. What We Do (paragraphs)
 * 4. Services Breakdown (4–6 cards)
 * 5. How It Works (3–4 steps)
 * 6. Why BRT (3 differentiators)
 * 7. Results / Proof (2 stat callouts + 1 case study placeholder)
 * 8. FAQs (accordion)
 * 9. Bottom CTA Banner
 */
export const ServicePageTemplate = ({ data, testIdSlug }) => {
  const accentClass = data.accentClass || 'from-[#2563EB] to-[#1D4ED8]';
  const lightAccent = data.lightAccentClass || 'bg-blue-50';
  const accentText = data.accentTextClass || 'text-[#2563EB]';
  const Icon = data.icon;

  // JSON-LD schema for AI Search Optimization (AEO/GEO)
  const serviceSchema = buildServiceSchema({
    name: data.heroHeadline,
    description: data.aiSummary || data.heroSubheadline,
    url: typeof window !== 'undefined' ? window.location.href : '',
    serviceType: data.eyebrow,
  });
  const faqSchema = data.faqs && data.faqs.length > 0 ? buildFAQSchema(data.faqs) : null;
  const breadcrumbSchema = data.breadcrumbs ? buildBreadcrumbSchema(data.breadcrumbs) : null;

  return (
    <div data-testid={`service-page-${testIdSlug}`}>
      {/* JSON-LD Schema for AI Search Engines */}
      <SchemaOrg data={serviceSchema} id={`schema-service-${testIdSlug}`} />
      {faqSchema && <SchemaOrg data={faqSchema} id={`schema-faq-${testIdSlug}`} />}
      {breadcrumbSchema && <SchemaOrg data={breadcrumbSchema} id={`schema-crumbs-${testIdSlug}`} />}
      {/* 1. HERO */}
      <section className="section-padding bg-[#0A2540] relative overflow-hidden">
        <AnimatedBlobs />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            {data.eyebrow && (
              <FadeInUp>
                <div
                  className={`inline-flex items-center gap-2 ${lightAccent} px-4 py-2 rounded-full mb-6`}
                >
                  {Icon && <Icon className={`w-4 h-4 ${accentText}`} />}
                  <span className={`text-sm font-medium ${accentText}`}>
                    {data.eyebrow}
                  </span>
                </div>
              </FadeInUp>
            )}
            <FadeInUp delay={0.1}>
              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {data.heroHeadline}
              </h1>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <p className="text-white/70 text-lg md:text-xl mb-8 max-w-2xl leading-relaxed">
                {data.heroSubheadline}
              </p>
            </FadeInUp>
            <FadeInUp delay={0.3}>
              <div className="flex flex-wrap gap-4">
                <Link to={data.primaryCta?.href || '/contact'}>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-6 text-base font-medium shadow-lg shadow-[#2563EB]/25"
                      data-testid={`hero-primary-cta-${testIdSlug}`}
                    >
                      {data.primaryCta?.label || 'Book a Call'}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </motion.div>
                </Link>
                {data.secondaryCta && (
                  <Link to={data.secondaryCta.href}>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        variant="outline"
                        className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white rounded-full px-8 py-6 text-base font-medium"
                        data-testid={`hero-secondary-cta-${testIdSlug}`}
                      >
                        {data.secondaryCta.label}
                      </Button>
                    </motion.div>
                  </Link>
                )}
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* 1b. AI ANSWER BLOCK, Direct-answer summary designed for AI search extraction (AEO/GEO) */}
      {data.aiSummary && (
        <section className="py-10 bg-slate-50 border-y border-slate-100" data-testid={`ai-summary-${testIdSlug}`}>
          <div className="container-custom">
            <div className="max-w-4xl mx-auto flex gap-4 items-start">
              <div className={`flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${accentClass} flex items-center justify-center`}>
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className={`text-xs font-semibold uppercase tracking-wider ${accentText} mb-1`}>
                  Quick Answer
                </div>
                <p className="text-slate-800 text-lg leading-relaxed">
                  {data.aiSummary}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 1c. STATS BAR, 4 KPIs for at-a-glance credibility */}
      {data.stats && data.stats.length > 0 && (
        <section className="py-12 bg-white border-b border-slate-100" data-testid={`stats-bar-${testIdSlug}`}>
          <div className="container-custom">
            <StaggerContainer
              className="grid grid-cols-2 md:grid-cols-4 gap-6"
              staggerDelay={0.08}
            >
              {data.stats.map((stat, i) => {
                const SIcon = stat.icon || TrendingUp;
                return (
                  <StaggerItem key={i}>
                    <div className="flex items-center gap-3" data-testid={`stat-${testIdSlug}-${i}`}>
                      <div className={`w-11 h-11 rounded-xl ${lightAccent} flex items-center justify-center flex-shrink-0`}>
                        <SIcon className={`w-5 h-5 ${accentText}`} />
                      </div>
                      <div>
                        <div className="text-2xl md:text-3xl font-bold text-[#0A2540]">
                          {stat.prefix || ''}
                          {typeof stat.value === 'number' ? (
                            <AnimatedCounter end={stat.value} suffix={stat.suffix || ''} duration={2} />
                          ) : (
                            <>{stat.value}{stat.suffix || ''}</>
                          )}
                        </div>
                        <div className="text-sm text-slate-600">{stat.label}</div>
                      </div>
                    </div>
                  </StaggerItem>
                );
              })}
            </StaggerContainer>
          </div>
        </section>
      )}

      {/* 2. PROBLEM BAR */}
      {data.problemBar && data.problemBar.length > 0 && (
        <section className="py-14 bg-white border-b border-slate-100">
          <div className="container-custom">
            <FadeInUp className="text-center mb-10">
              <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">
                Sound Familiar?
              </span>
              <h2 className="font-outfit text-2xl md:text-3xl font-bold text-[#0A2540] mt-2">
                The problems we hear every week
              </h2>
            </FadeInUp>
            <StaggerContainer
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
              staggerDelay={0.1}
            >
              {data.problemBar.map((problem, i) => (
                <StaggerItem key={i}>
                  <motion.div
                    className="bg-slate-50 border border-slate-100 p-6 rounded-2xl h-full flex gap-4"
                    whileHover={{ y: -4, borderColor: '#fecaca' }}
                    data-testid={`problem-${testIdSlug}-${i}`}
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-rose-600" />
                    </div>
                    <p className="text-slate-700 font-medium leading-relaxed">
                      “{problem}”
                    </p>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      )}

      {/* 3. WHAT WE DO */}
      {data.whatWeDo && (
        <section className="section-padding">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              <FadeInUp>
                <span className={`text-sm font-semibold ${accentText} uppercase tracking-wider`}>
                  What We Do
                </span>
                <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540] mt-2 mb-6">
                  {data.whatWeDoHeadline || 'Plain-English overview'}
                </h2>
              </FadeInUp>
              {data.whatWeDo.map((para, i) => (
                <FadeInUp key={i} delay={0.1 + i * 0.05}>
                  <p className="text-lg text-slate-700 leading-relaxed mb-5">{para}</p>
                </FadeInUp>
              ))}
              {data.whatWeDoCallout && (
                <FadeInUp delay={0.3}>
                  <div
                    className={`mt-8 p-6 rounded-2xl bg-gradient-to-br ${accentClass} text-white shadow-lg`}
                  >
                    <Sparkles className="w-6 h-6 mb-3 opacity-90" />
                    <p className="text-white text-lg font-medium leading-relaxed">
                      {data.whatWeDoCallout}
                    </p>
                  </div>
                </FadeInUp>
              )}
            </div>
          </div>
        </section>
      )}

      {/* 4. SERVICES BREAKDOWN */}
      {data.servicesBreakdown && data.servicesBreakdown.length > 0 && (
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <FadeInUp className="text-center mb-12">
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540]">
                {data.servicesBreakdownTitle || 'What We Offer'}
              </h2>
              {data.servicesBreakdownSub && (
                <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
                  {data.servicesBreakdownSub}
                </p>
              )}
            </FadeInUp>
            <StaggerContainer
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              staggerDelay={0.08}
            >
              {data.servicesBreakdown.map((s, i) => {
                const SIcon = s.icon;
                const Wrapper = s.href ? Link : 'div';
                return (
                  <StaggerItem key={i}>
                    <Wrapper {...(s.href ? { to: s.href } : {})}>
                      <motion.div
                        className="bg-white p-6 rounded-2xl border border-slate-100 h-full hover:shadow-xl hover:border-slate-200 transition-all group cursor-pointer"
                        whileHover={{ y: -5 }}
                        data-testid={`service-card-${testIdSlug}-${i}`}
                      >
                        {SIcon && (
                          <motion.div
                            className={`w-12 h-12 bg-gradient-to-br ${accentClass} rounded-xl flex items-center justify-center mb-4`}
                            whileHover={{ scale: 1.08, rotate: 5 }}
                          >
                            <SIcon className="w-6 h-6 text-white" />
                          </motion.div>
                        )}
                        <h3 className="font-outfit text-lg font-semibold text-[#0A2540] mb-2 group-hover:text-[#2563EB] transition-colors">
                          {s.title}
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed mb-4">
                          {s.description}
                        </p>
                        {s.href && (
                          <span className={`text-sm font-medium ${accentText} flex items-center gap-1 group-hover:gap-2 transition-all`}>
                            Learn more <ArrowRight className="w-4 h-4" />
                          </span>
                        )}
                      </motion.div>
                    </Wrapper>
                  </StaggerItem>
                );
              })}
            </StaggerContainer>
            {data.platformBadges && (
              <FadeInUp delay={0.2} className="mt-12 text-center">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-4">
                  Platforms We Work With
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {data.platformBadges.map((p, i) => (
                    <span
                      key={i}
                      className="px-4 py-2 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </FadeInUp>
            )}
          </div>
        </section>
      )}

      {/* 5. HOW IT WORKS */}
      {data.howItWorks && data.howItWorks.length > 0 && (
        <section className={`section-padding bg-gradient-to-br ${accentClass}`}>
          <div className="container-custom">
            <FadeInUp className="text-center mb-12">
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white">
                How It Works
              </h2>
              <p className="text-white/80 mt-3">A clear path from kickoff to results</p>
            </FadeInUp>
            <StaggerContainer
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${data.howItWorks.length} gap-6`}
              staggerDelay={0.12}
            >
              {data.howItWorks.map((step, i) => (
                <StaggerItem key={i}>
                  <motion.div
                    className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 rounded-2xl text-center relative h-full"
                    whileHover={{ y: -5, backgroundColor: 'rgba(255,255,255,0.15)' }}
                    data-testid={`step-${testIdSlug}-${i}`}
                  >
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-lg font-bold text-[#0A2540]">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h3 className="font-outfit text-lg font-semibold text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="text-white/80 text-sm leading-relaxed">
                      {step.description}
                    </p>
                    {i < data.howItWorks.length - 1 && (
                      <div className="hidden lg:block absolute top-1/2 -right-3 -translate-y-1/2">
                        <ChevronRight className="w-6 h-6 text-white/50" />
                      </div>
                    )}
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      )}

      {/* 6. WHY BRT */}
      {data.whyBRT && data.whyBRT.length > 0 && (
        <section className="section-padding">
          <div className="container-custom">
            <FadeInUp className="text-center mb-12">
              <span className={`text-sm font-semibold ${accentText} uppercase tracking-wider`}>
                Why Blue Ribbon
              </span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540] mt-2">
                What makes us different
              </h2>
            </FadeInUp>
            <StaggerContainer
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              staggerDelay={0.1}
            >
              {data.whyBRT.map((diff, i) => (
                <StaggerItem key={i}>
                  <motion.div
                    className="text-center p-6"
                    whileHover={{ y: -5 }}
                    data-testid={`why-brt-${testIdSlug}-${i}`}
                  >
                    <div
                      className={`w-14 h-14 mx-auto mb-5 rounded-2xl bg-gradient-to-br ${accentClass} flex items-center justify-center`}
                    >
                      <CheckCircle2 className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-3">
                      {diff.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">{diff.description}</p>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      )}

      {/* 7. RESULTS / PROOF */}
      {data.proof && (
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <FadeInUp className="text-center mb-12">
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540]">
                Real Results, Not Fluff
              </h2>
            </FadeInUp>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {/* Stat Callouts */}
              {data.proof.stats?.map((stat, i) => (
                <FadeInUp key={i} delay={0.1 + i * 0.1}>
                  <motion.div
                    className={`bg-gradient-to-br ${accentClass} text-white p-8 rounded-2xl h-full flex flex-col justify-center`}
                    whileHover={{ y: -5 }}
                    data-testid={`proof-stat-${testIdSlug}-${i}`}
                  >
                    <TrendingUp className="w-8 h-8 mb-4 opacity-80" />
                    <div className="text-4xl md:text-5xl font-bold mb-2">
                      {stat.prefix || ''}
                      <AnimatedCounter end={stat.value} suffix={stat.suffix || ''} duration={2} />
                    </div>
                    <div className="text-white/90 text-base">{stat.label}</div>
                  </motion.div>
                </FadeInUp>
              ))}
              {/* Case Study Placeholder */}
              {data.proof.caseStudy && (
                <FadeInUp delay={0.3}>
                  <motion.div
                    className="bg-white p-8 rounded-2xl border border-slate-200 h-full flex flex-col justify-between"
                    whileHover={{ y: -5, borderColor: '#2563EB' }}
                    data-testid={`proof-casestudy-${testIdSlug}`}
                  >
                    <div>
                      <Quote className={`w-8 h-8 mb-4 ${accentText} opacity-60`} />
                      <p className="text-slate-700 leading-relaxed mb-4 italic">
                        “{data.proof.caseStudy.quote}”
                      </p>
                      <div className="text-sm font-semibold text-[#0A2540]">
                        {data.proof.caseStudy.client}
                      </div>
                      <div className="text-xs text-slate-500">
                        {data.proof.caseStudy.industry}
                      </div>
                    </div>
                    <Link
                      to="/case-studies"
                      className={`text-sm font-medium ${accentText} flex items-center gap-1 mt-4 hover:gap-2 transition-all`}
                    >
                      Read full case study <ArrowRight className="w-4 h-4" />
                    </Link>
                  </motion.div>
                </FadeInUp>
              )}
            </div>
          </div>
        </section>
      )}

      {/* 7b. TRUST STRIP, 4 concrete BRT commitments */}
      <section className="py-14 bg-white border-y border-slate-100" data-testid={`trust-strip-${testIdSlug}`}>
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto text-center">
            {[
              { icon: MapPin, label: 'BC-Based Team', sub: 'Local, in Vancouver' },
              { icon: FileCheck, label: 'BC & Canadian Law', sub: 'Transparent contracts' },
              { icon: Lock, label: 'You Own Your IP', sub: 'Clear IPR handoff' },
              { icon: ShieldCheck, label: 'NDA-Backed', sub: 'Confidential by default' },
            ].map((t, i) => (
              <div key={i} className="flex flex-col items-center gap-2" data-testid={`trust-${testIdSlug}-${i}`}>
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accentClass} flex items-center justify-center`}>
                  <t.icon className="w-5 h-5 text-white" />
                </div>
                <div className="font-semibold text-[#0A2540] text-sm">{t.label}</div>
                <div className="text-xs text-slate-500">{t.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. FAQs */}
      {data.faqs && data.faqs.length > 0 && (
        <section className="section-padding">
          <div className="container-custom">
            <FadeInUp className="text-center mb-12">
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540]">
                Frequently Asked Questions
              </h2>
            </FadeInUp>
            <FadeInUp delay={0.2} className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                {data.faqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    className="bg-white border border-slate-100 rounded-2xl px-6 data-[state=open]:shadow-lg transition-shadow"
                    data-testid={`faq-${testIdSlug}-${i}`}
                  >
                    <AccordionTrigger className="text-left font-medium text-[#0A2540] hover:no-underline py-6">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 pb-6 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </FadeInUp>
          </div>
        </section>
      )}

      {/* 9. BOTTOM CTA */}
      <section className="section-padding bg-[#0A2540] relative overflow-hidden">
        <AnimatedBlobs />
        <div className="container-custom text-center relative z-10">
          <FadeInUp>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-white mb-6 max-w-3xl mx-auto leading-tight">
              {data.bottomCta?.headline || 'Ready to get started?'}
            </h2>
          </FadeInUp>
          <FadeInUp delay={0.3}>
            <Link to={data.bottomCta?.href || '/contact'}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="inline-block"
              >
                <Button
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-10 py-6 text-lg font-medium shadow-xl shadow-[#2563EB]/25"
                  data-testid={`bottom-cta-${testIdSlug}`}
                >
                  {data.bottomCta?.label || 'Book a Free Call'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </Link>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default ServicePageTemplate;
